import { useEffect, useState } from 'react'
import { formatCents, parseAmountToCents } from '@core/money'
import type { Space } from '@core/session'
import { setSpaceShared } from '@core/expenses'
import { loadSpaceSettings, saveSpaceSettings, type SpaceSettings as Settings } from '@core/spaceSettings'
import { Button, Card, ErrorNote } from '@design/components'

/**
 * Per space: how far ahead you pay, and how much the app may wave through.
 *
 * The ceiling is the most consequential number in the product — it is the
 * amount above which nothing is ever auto-accepted, however well known the
 * supplier. So it is stated in euros the way you would say it out loud, and
 * the consequence is written next to it rather than left implied.
 */
export function SpaceSettingsCard({
  space,
  userId,
  shared,
  onSharedChange,
}: {
  space: Space
  userId: string
  shared?: boolean
  onSharedChange?: () => void
}) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [ceiling, setCeiling] = useState('')
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadSpaceSettings(space.id)
      .then((s) => {
        setSettings(s)
        setCeiling((s.greenLaneCeilingCents / 100).toFixed(2))
      })
      .catch(() => setSettings(null))
  }, [space.id])

  if (!settings) return null

  async function save() {
    const cents = parseAmountToCents(ceiling)
    if (cents === null) {
      setError('Dat bedrag begrijp ik niet.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await saveSpaceSettings(space, { ...settings!, greenLaneCeilingCents: cents }, userId)
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
    }
    setBusy(false)
  }

  return (
    <Card className="mb-3">
      <p className="font-medium mb-3">{space.name}</p>

      <label className="block mb-3">
        <span className="block text-sm text-muted mb-1.5">
          Hoeveel dagen op voorhand betalen
        </span>
        <input
          type="number"
          min={0}
          max={30}
          value={settings.leadDays}
          onChange={(e) => {
            setSettings({ ...settings, leadDays: Number(e.target.value) })
            setSaved(false)
          }}
          className="w-24 min-h-[3rem] px-3 rounded-xl bg-canvas border border-line tabular-nums"
        />
        <span className="block text-xs text-muted mt-1.5">
          Bepaalt op welke dag een factuur in je planning verschijnt. De
          vervaldag zelf verandert niet.
        </span>
      </label>

      <label className="block">
        <span className="block text-sm text-muted mb-1.5">
          Zelf nakijken vanaf
        </span>
        <input
          value={ceiling}
          onChange={(e) => {
            setCeiling(e.target.value)
            setSaved(false)
          }}
          inputMode="decimal"
          className="w-32 min-h-[3rem] px-3 rounded-xl bg-canvas border border-line tabular-nums"
        />
        <span className="block text-xs text-muted mt-1.5">
          Onder {formatCents(settings.greenLaneCeilingCents)} mag een factuur van
          een bekende leverancier er zonder jou door. Daarboven kijk jij er
          altijd naar, hoe vertrouwd de leverancier ook is.
        </span>
      </label>

      <label className="flex items-start justify-between gap-3 py-2 mt-2 border-t border-line">
        <span className="text-sm">
          De rest van het gezin mag meekijken
          <span className="block text-xs text-muted mt-0.5">
            Alleen in deze ruimte, en alleen lezen.
          </span>
        </span>
        <input
          type="checkbox"
          checked={Boolean(shared)}
          onChange={async (e) => {
            try {
              await setSpaceShared(space.id, e.target.checked)
              onSharedChange?.()
            } catch {
              setError('Dat lukte niet.')
            }
          }}
          className="w-6 h-6 shrink-0 mt-0.5"
        />
      </label>

      <ErrorNote>{error}</ErrorNote>

      <div className="mt-3">
        <Button tone="secondary" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : saved ? 'Opgeslagen' : 'Opslaan'}
        </Button>
      </div>
    </Card>
  )
}
